/* UV Add to Cart Confetti v1.3 — Horizon 4.1.5+ */
(() => {
  'use strict';

  const GLOBAL_KEY = '__uvAtcConfettiManagerV1';
  const CART_LINES_EVENT = 'shopify:cart:lines-update';
  const CART_ERROR_EVENT = 'shopify:cart:error';
  const ADD_BUTTON_SELECTOR = 'button[name="add"], input[name="add"], button[type="submit"], input[type="submit"]';
  const RECENT_SUBMIT_MS = 2500;
  const DRAWER_SELECTOR = 'theme-drawer#cart-drawer';
  const DRAWER_COMPONENT_SELECTOR = '#cart-drawer cart-drawer-component';
  const SUPPRESSED_ATTR = 'data-uv-atc-native-auto-open';

  if (window[GLOBAL_KEY]?.destroy) window[GLOBAL_KEY].destroy();

  let destroyed = false;
  let config = null;
  let recentAdd = null;
  let confettiFrame = 0;
  let drawerOpenTimer = 0;
  let drawerObserver = null;
  const successTimers = new Map();
  const canvases = new Set();

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const readConfig = () => {
    const nodes = [...document.querySelectorAll('[data-uv-atc-confetti-config]')];
    for (const node of nodes) {
      try {
        const parsed = JSON.parse(node.textContent || '{}');
        if (parsed?.enabled) return parsed;
      } catch (_) {}
    }
    return null;
  };

  const getDrawerComponent = () => document.querySelector(DRAWER_COMPONENT_SELECTOR);

  const suppressNativeAutoOpen = () => {
    const component = getDrawerComponent();
    if (!(component instanceof HTMLElement)) return;

    // The confetti add-on owns drawer timing only while its drawer-open
    // feature is enabled. Remember Horizon's native state so it can be
    // restored cleanly if the section is disabled/unloaded in Theme Editor.
    if (component.hasAttribute('auto-open')) {
      component.setAttribute(SUPPRESSED_ATTR, 'true');
      component.removeAttribute('auto-open');
    }
  };

  const restoreNativeAutoOpen = () => {
    const component = getDrawerComponent();
    if (!(component instanceof HTMLElement)) return;
    if (!component.hasAttribute(SUPPRESSED_ATTR)) return;

    component.setAttribute('auto-open', '');
    component.removeAttribute(SUPPRESSED_ATTR);
  };

  const syncDrawerAutoOpenOwnership = () => {
    if (config?.enabled && config?.openCartDrawer) suppressNativeAutoOpen();
    else restoreNativeAutoOpen();
  };

  const startDrawerObserver = () => {
    drawerObserver?.disconnect();
    drawerObserver = null;

    const drawer = document.querySelector(DRAWER_SELECTOR);
    if (!(drawer instanceof HTMLElement)) return;

    drawerObserver = new MutationObserver((records) => {
      if (destroyed || !config?.enabled || !config?.openCartDrawer) return;

      for (const record of records) {
        if (record.type === 'attributes' || record.addedNodes.length || record.removedNodes.length) {
          suppressNativeAutoOpen();
          break;
        }
      }
    });

    drawerObserver.observe(drawer, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['auto-open'],
    });
  };

  const refreshConfig = () => {
    config = readConfig();
    syncDrawerAutoOpenOwnership();
    startDrawerObserver();
  };

  const isCartAddForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    return (form.getAttribute('action') || '').includes('/cart/add');
  };

  const getAddButton = (form, submitter = null) => {
    if (submitter instanceof HTMLElement && submitter.matches(ADD_BUTTON_SELECTOR)) return submitter;
    if (!(form instanceof HTMLFormElement)) return null;
    const active = document.activeElement;
    if (active instanceof HTMLElement && form.contains(active) && active.matches(ADD_BUTTON_SELECTOR)) return active;
    return form.querySelector(ADD_BUTTON_SELECTOR);
  };

  const cleanupCanvas = (canvas, instance = null) => {
    if (!(canvas instanceof HTMLCanvasElement)) return;
    try { instance?.reset?.(); } catch (_) {}
    canvases.delete(canvas);
    canvas.remove();
  };

  const fireConfetti = (button) => {
    if (!config?.enabled || !(button instanceof Element)) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    if (typeof window.confetti?.create !== 'function') return;

    const rect = button.getBoundingClientRect();
    const x = clamp((rect.left + rect.width / 2) / Math.max(1, window.innerWidth), 0, 1);
    const y = clamp((rect.top + rect.height / 2) / Math.max(1, window.innerHeight), 0, 1);

    const canvas = document.createElement('canvas');
    canvas.className = 'uv-atc-confetti-canvas';
    canvas.setAttribute('aria-hidden', 'true');

    // Native dialogs live in the browser top layer. Mount inside the same
    // open dialog so quick-add confetti cannot render behind it.
    const host = button.closest('dialog[open]') || document.body;
    host.appendChild(canvas);
    canvases.add(canvas);

    let instance = null;
    try {
      instance = window.confetti.create(canvas, { resize: true, useWorker: true });
      instance({
        particleCount: Number(config.particleCount) || 100,
        spread: Number(config.spread) || 70,
        origin: { x, y },
        colors: [
          config.color1 || '#00C853',
          config.color2 || '#000000',
          config.color3 || '#B565A7',
          config.color4 || '#FFE600',
          config.color5 || '#FF5722',
          config.color6 || '#FF007F',
          config.color7 || '#00E5FF',
          config.color8 || '#FFFFFF',
        ].slice(0, clamp(Math.floor(Number(config.colorCount) || 3), 1, 8)),
        disableForReducedMotion: true,
      });
    } catch (_) {
      cleanupCanvas(canvas, instance);
      return;
    }

    window.setTimeout(() => cleanupCanvas(canvas, instance), 2600);
  };

  const scheduleConfetti = (button) => {
    if (!(button instanceof Element)) return;
    if (confettiFrame) cancelAnimationFrame(confettiFrame);
    confettiFrame = requestAnimationFrame(() => {
      confettiFrame = 0;
      fireConfetti(button);
    });
  };

  const clearSuccess = (button) => {
    if (!(button instanceof HTMLElement)) return;
    const timer = successTimers.get(button);
    if (timer) clearTimeout(timer);
    successTimers.delete(button);
    button.classList.remove('uv-atc-confetti-success');
    button.style.removeProperty('--uv-atc-success-color');
  };

  const showSuccess = (button) => {
    if (!config?.showSuccess || !(button instanceof HTMLElement) || !button.isConnected) return;
    clearSuccess(button);
    button.style.setProperty('--uv-atc-success-color', config.successColor || '#28A745');
    button.classList.add('uv-atc-confetti-success');
    const duration = Math.max(0, Number(config.successDuration) || 1800);
    const timer = window.setTimeout(() => {
      successTimers.delete(button);
      if (button.isConnected) {
        button.classList.remove('uv-atc-confetti-success');
        button.style.removeProperty('--uv-atc-success-color');
      }
    }, duration);
    successTimers.set(button, timer);
  };

  const resultFailed = (result) => {
    return Boolean(result?.detail?.didError) ||
      (Array.isArray(result?.userErrors) && result.userErrors.length > 0) ||
      (!result?.cart && !result?.detail?.items && result?.detail?.didError !== false);
  };

  const openCartDrawer = (sourceModal = null) => {
    if (destroyed || !config?.enabled || !config?.openCartDrawer) return;

    const drawer = document.querySelector(DRAWER_SELECTOR);
    if (!(drawer instanceof HTMLElement) || typeof drawer.open !== 'function') return;

    // Never replay Horizon's drawer-open animation if it is already visible.
    if (drawer.hasAttribute('open') || drawer.isOpen === true) return;

    const actuallyOpen = () => {
      if (destroyed || !config?.enabled || !config?.openCartDrawer) return;
      const currentDrawer = document.querySelector(DRAWER_SELECTOR);
      if (!(currentDrawer instanceof HTMLElement) || typeof currentDrawer.open !== 'function') return;
      if (currentDrawer.hasAttribute('open') || currentDrawer.isOpen === true) return;

      // One frame gives Horizon's cart morph a chance to commit before the
      // drawer becomes visible, especially when delay is set to 0 ms.
      requestAnimationFrame(() => {
        if (destroyed || !config?.enabled || !config?.openCartDrawer) return;
        if (!currentDrawer.isConnected || currentDrawer.hasAttribute('open') || currentDrawer.isOpen === true) return;
        currentDrawer.open();
      });
    };

    // Match Horizon's own quick-add safety: don't open a second modal drawer
    // until the source modal has completed its native close/focus restore.
    if (sourceModal instanceof HTMLDialogElement && sourceModal.open && sourceModal.matches(':modal')) {
      sourceModal.addEventListener('close', actuallyOpen, { once: true });
      return;
    }

    actuallyOpen();
  };

  const scheduleCartDrawerOpen = (sourceModal = null, startedAt = null) => {
    if (!config?.openCartDrawer) return;
    if (drawerOpenTimer) clearTimeout(drawerOpenTimer);

    // The configured delay is a TARGET measured from the original Add to Cart
    // action, not an extra wait added after Shopify confirms success.
    // Example with a 500 ms target:
    //   Shopify confirms in 200 ms -> wait 300 ms more.
    //   Shopify confirms in 700 ms -> open immediately on confirmation.
    const targetDelay = clamp(Number(config.drawerDelay) || 0, 0, 10000);
    const start = Number.isFinite(startedAt) ? startedAt : performance.now();
    const elapsed = Math.max(0, performance.now() - start);
    const remaining = Math.max(0, targetDelay - elapsed);

    if (remaining <= 0) {
      drawerOpenTimer = 0;
      openCartDrawer(sourceModal);
      return;
    }

    drawerOpenTimer = window.setTimeout(() => {
      drawerOpenTimer = 0;
      openCartDrawer(sourceModal);
    }, remaining);
  };

  const findButtonForAddEvent = (event) => {
    const target = event?.target instanceof Element ? event.target : null;
    const recent = recentAdd;

    if (recent && performance.now() - recent.at < RECENT_SUBMIT_MS) {
      const sameSource = !target || target.contains(recent.form) || recent.form.contains(target) || target.closest?.('form') === recent.form;
      if (sameSource || event?.context === 'product' || event?.context === 'dialog') {
        recentAdd = null;
        return {
          button: recent.button,
          form: recent.form,
          sourceModal: recent.sourceModal,
          confettiAlreadyFired: recent.confettiFired,
          startedAt: recent.at,
        };
      }
    }

    const active = document.activeElement;
    if (active instanceof HTMLElement && active.matches(ADD_BUTTON_SELECTOR)) {
      return {
        button: active,
        form: active.closest('form'),
        sourceModal: active.closest('dialog:modal'),
        confettiAlreadyFired: false,
        startedAt: performance.now(),
      };
    }

    const button = target?.querySelector?.(ADD_BUTTON_SELECTOR) || target?.closest?.(ADD_BUTTON_SELECTOR) || null;
    return {
      button,
      form: button?.closest?.('form') || null,
      sourceModal: target?.closest?.('dialog:modal') || button?.closest?.('dialog:modal') || null,
      confettiAlreadyFired: false,
      startedAt: performance.now(),
    };
  };

  const onSubmit = (event) => {
    if (!config?.enabled) return;
    const form = event.target;
    if (!isCartAddForm(form)) return;

    // Ensure Horizon's own immediate auto-open cannot race the configured
    // UV drawer delay even if the drawer section was just morphed.
    if (config?.openCartDrawer) suppressNativeAutoOpen();

    const button = getAddButton(form, event.submitter);
    if (!(button instanceof HTMLElement) || button.getAttribute('name') === 'checkout' || button.disabled) return;

    if (recentAdd?.button && recentAdd.button !== button) clearSuccess(recentAdd.button);
    recentAdd = {
      button,
      form,
      sourceModal: form.closest('dialog:modal'),
      at: performance.now(),
      confettiFired: true,
    };

    // Celebration starts immediately after submit, but one frame later so
    // Horizon's original submit handler is never blocked by canvas work.
    scheduleConfetti(button);
  };

  const onCartLinesUpdate = (event) => {
    if (!config?.enabled || event?.action !== 'add') return;

    // A third-party add may not have emitted a regular form submit. Keep our
    // ownership of auto-open timing synchronized before any future add event.
    if (config?.openCartDrawer) suppressNativeAutoOpen();

    const match = findButtonForAddEvent(event);
    const button = match?.button;

    // Handles non-form/third-party ADD publishers while avoiding duplicate
    // confetti when the delegated submit listener already fired it.
    if (button instanceof HTMLElement && !match.confettiAlreadyFired) scheduleConfetti(button);

    const promise = event?.promise;
    if (!promise?.then) return;

    promise
      .then((result) => {
        if (resultFailed(result)) {
          if (button instanceof HTMLElement) clearSuccess(button);
          return;
        }

        if (button instanceof HTMLElement) showSuccess(button);
        scheduleCartDrawerOpen(match?.sourceModal || null, match?.startedAt);
      })
      .catch(() => {
        if (button instanceof HTMLElement) clearSuccess(button);
      });
  };

  const onCartError = () => {
    if (recentAdd?.button) clearSuccess(recentAdd.button);
    if (drawerOpenTimer) {
      clearTimeout(drawerOpenTimer);
      drawerOpenTimer = 0;
    }
  };

  const onSectionChange = (event) => {
    if (!(event?.target instanceof Element)) return;
    if (
      event.target.matches?.('[data-uv-atc-confetti-config]') ||
      event.target.querySelector?.('[data-uv-atc-confetti-config]') ||
      event.target.matches?.('#shopify-section-cart-drawer-section') ||
      event.target.querySelector?.('#cart-drawer')
    ) {
      refreshConfig();
    }
  };

  const destroy = () => {
    if (destroyed) return;
    destroyed = true;
    document.removeEventListener('submit', onSubmit, true);
    document.removeEventListener(CART_LINES_EVENT, onCartLinesUpdate);
    document.removeEventListener(CART_ERROR_EVENT, onCartError);
    document.removeEventListener('shopify:section:load', onSectionChange);
    document.removeEventListener('shopify:section:unload', onSectionChange);

    drawerObserver?.disconnect();
    drawerObserver = null;
    restoreNativeAutoOpen();

    if (confettiFrame) cancelAnimationFrame(confettiFrame);
    confettiFrame = 0;

    if (drawerOpenTimer) clearTimeout(drawerOpenTimer);
    drawerOpenTimer = 0;

    for (const [button, timer] of successTimers) {
      clearTimeout(timer);
      if (button?.isConnected) {
        button.classList.remove('uv-atc-confetti-success');
        button.style.removeProperty('--uv-atc-success-color');
      }
    }
    successTimers.clear();

    for (const canvas of [...canvases]) cleanupCanvas(canvas);
    canvases.clear();
    recentAdd = null;
  };

  refreshConfig();
  document.addEventListener('submit', onSubmit, true);
  document.addEventListener(CART_LINES_EVENT, onCartLinesUpdate);
  document.addEventListener(CART_ERROR_EVENT, onCartError);
  document.addEventListener('shopify:section:load', onSectionChange);
  document.addEventListener('shopify:section:unload', onSectionChange);

  window[GLOBAL_KEY] = { destroy, refresh: refreshConfig };
})();
